import React, { useMemo, useState } from "react";
import jsonExport from "jsonexport/dist";

import { startOfMonth, subMonths } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { productByAccountByActiveByName } from "../../../graphql/queries";

import {
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Paper,
  TextField as FilterField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Datagrid,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  required,
  SimpleForm,
  TextField,
  Title,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";
import { useHistory } from "react-router-dom";

import { ExportIcon } from "../../../components/common/iconography";
import { HelpButton } from "../../../components/common/HelpButton";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { MonthRangeSlider } from "../../../components/common/MonthRangeSlider";
import { Pagination } from "../../../components/Pagination";

import { logError } from "../../../utils/appUtils";
import { deepRedirect } from "../../../utils/utils";
import { AccountDropDown } from "../../../components/common/AccountDropDown";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  filterBar: {
    marginTop: theme.spacing(-4),
    marginBottom: theme.spacing(1),
  },
  stickyCol: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  stickyHeader: {
    position: "sticky",
    left: 0,
    zIndex: 3,
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  kpiCard: {
    padding: theme.spacing(1.5),
    height: "100%",
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: 18,
  },
  chartPaper: {
    padding: theme.spacing(1.5),
  },
}));

const pad2 = (n) => String(n).padStart(2, "0");

const monthKeyFromDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
};

const parseStats = (stats) => {
  if (!stats) return {};
  if (typeof stats === "object") return stats;
  if (typeof stats !== "string") return {};
  try {
    const parsed = JSON.parse(stats);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const compareMonthKeys = (a, b) => (a === b ? 0 : a < b ? -1 : 1);

const listMonthKeysInclusive = (startMonth, endMonth) => {
  if (!startMonth || !endMonth) return [];
  if (compareMonthKeys(startMonth, endMonth) > 0) return [];

  const [sy, sm] = startMonth.split("-").map((x) => Number(x));
  const [ey, em] = endMonth.split("-").map((x) => Number(x));
  if (!sy || !sm || !ey || !em) return [];

  const months = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${y}-${pad2(m)}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    if (months.length > 24) break; // safety
  }
  return months;
};

const daysInMonthUtc = (monthKey) => {
  const [y, m] = monthKey.split("-").map((x) => Number(x));
  if (!y || !m) return 0;
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

const daysInMonthRangeInclusive = (startMonth, endMonth) => {
  const months = listMonthKeysInclusive(startMonth, endMonth);
  return months.reduce((sum, mk) => sum + daysInMonthUtc(mk), 0);
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const percentile = (sortedNumbers, p) => {
  if (!sortedNumbers.length) return null;
  const idx = (sortedNumbers.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedNumbers[lo];
  const w = idx - lo;
  return sortedNumbers[lo] * (1 - w) + sortedNumbers[hi] * w;
};

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const heatColor = ({ value, min, max, invert = false }) => {
  // Red ↔ Green gradient; invert flips meaning (e.g. lower days = "good").
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max))
    return "transparent";
  if (min === max) return "transparent";
  const t = clamp01((value - min) / (max - min));
  const x = invert ? 1 - t : t; // x=0 => green, x=1 => red
  const r = Math.round(220 * x + 40 * (1 - x));
  const g = Math.round(220 * (1 - x) + 40 * x);
  return `rgba(${r}, ${g}, 80, 0.22)`;
};

const formatNumber = (n, digits = 2) => {
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const buildVelocityRows = ({
  products,
  reloadYardCommodityId,
  startMonth,
  endMonth,
}) => {
  const monthKeys = listMonthKeysInclusive(startMonth, endMonth);
  const daysInRange = daysInMonthRangeInclusive(startMonth, endMonth);

  const filteredProducts = reloadYardCommodityId
    ? products.filter((p) => p.reloadYardCommodityId === reloadYardCommodityId)
    : products;

  return filteredProducts.map((p) => {
    const stats = parseStats(p.velocityStats?.stats);

    let totalReceived = 0;
    let totalShipped = 0;
    let totalInventoryDays = 0;
    let totalDaysTracked = 0;
    let totalTurnDays = 0;
    let completedLots = 0;
    let outboundDays = 0;

    monthKeys.forEach((mk) => {
      const b = stats?.[mk];
      if (!b) return;
      totalReceived += toNumber(b.receivedQty);
      totalShipped += toNumber(b.shippedQty);
      totalInventoryDays += toNumber(b.inventoryDays);
      totalDaysTracked += toNumber(b.daysTracked);
      totalTurnDays += toNumber(b.totalTurnDays);
      completedLots += toNumber(b.completedLots);
      outboundDays += toNumber(b.outboundDays);
    });

    const avgInventory =
      totalDaysTracked > 0 ? totalInventoryDays / totalDaysTracked : 0;

    // IMPORTANT: avgDaysInStorage requires completedLots; otherwise it is "unknown".
    const avgDaysInStorage =
      completedLots > 0 ? totalTurnDays / completedLots : null;
    const avgDaysInStorageSort =
      avgDaysInStorage == null ? Number.POSITIVE_INFINITY : avgDaysInStorage;

    const inventoryTurnover =
      avgInventory > 0 ? totalShipped / avgInventory : 0;

    const throughput = daysInRange > 0 ? totalShipped / daysInRange : 0;

    const sellThroughRate =
      totalReceived > 0 ? totalShipped / totalReceived : 0;

    const avgDailyOutbound = outboundDays > 0 ? totalShipped / outboundDays : 0;

    return {
      id: p.id,
      name: p.name,
      productCode: p.productCode,
      reloadYardCommodityId: p.reloadYardCommodityId,
      defaultUOM: p.defaultUOM,
      statsUpdatedAt: p.velocityStats?.updatedAt,

      totalReceived,
      totalShipped,
      avgInventory,
      outboundDays,
      completedLots,
      totalTurnDays,
      daysInRange,

      inventoryTurnover,
      avgDaysInStorage,
      avgDaysInStorageSort,
      throughput,
      sellThroughRate,
      sellThroughPct: sellThroughRate * 100,
      avgDailyOutbound,

      // filled later client-side
      velocityTier: "MEDIUM",
      atRisk: false,
      presetRank: 0,
    };
  });
};

const applyVelocityTiering = (rows) => {
  // Tiering is based on percentiles of avgDaysInStorage (ignoring unknowns).
  const days = rows
    .map((r) => r.avgDaysInStorage)
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  const p33 = percentile(days, 0.33);
  const p66 = percentile(days, 0.66);
  const p75 = percentile(days, 0.75);

  return rows.map((r) => {
    const v = r.avgDaysInStorage;
    const atRisk = Number.isFinite(v) && p75 != null ? v >= p75 : false;

    let velocityTier = "MEDIUM";
    if (!Number.isFinite(v) || p33 == null || p66 == null) {
      // Unknown days (no completed lots) is treated as MEDIUM by default; still sortable.
      velocityTier = "MEDIUM";
    } else if (v <= p33) {
      velocityTier = "FAST";
    } else if (v >= p66) {
      velocityTier = "SLOW";
    }

    return { ...r, velocityTier, atRisk };
  });
};

// Default sort/ranking for this report:
// - Primary: avgDaysInStorage ASC (unknowns last)
// - Secondary: inventoryTurnover DESC
// Users can still override by clicking any column header after running the report.
const applyDefaultRanking = (rows) => {
  const withRank = [...rows];

  withRank.sort((a, b) => {
    const dayCmp = a.avgDaysInStorageSort - b.avgDaysInStorageSort;
    if (dayCmp !== 0) return dayCmp;
    return (b.inventoryTurnover || 0) - (a.inventoryTurnover || 0);
  });

  return withRank.map((r, idx) => ({ ...r, presetRank: idx + 1 }));
};

const exporter = (rows) => {
  const exportRows = rows.map((r) => ({
    productName: r.name,
    productCode: r.productCode,
    loadTypeId: r.reloadYardCommodityId,
    inventoryTurnover: r.inventoryTurnover,
    avgDaysInStorage: r.avgDaysInStorage,
    throughputPerDay: r.throughput,
    sellThroughPct: r.sellThroughPct,
    avgInventoryOnHand: r.avgInventory,
    totalShipped: r.totalShipped,
    totalReceived: r.totalReceived,
    velocityTier: r.velocityTier,
    statsUpdatedAt: r.statsUpdatedAt,
  }));

  jsonExport(exportRows, {}, (err, csv) => {
    downloadCSV(csv, "ProductVelocityReport");
  });
};

const FilterBar = (props) => {
  const classes = useStyles();
  const {
    rows,
    loading,
    accountId,
    handleAccountChange,
    loadTypeOptions,
    reloadYardCommodityId,
    setReloadYardCommodityId,
    monthRange,
    setMonthRange,
    chartMetric,
    setChartMetric,
    onRun,
  } = props;

  return (
    <Box className={classes.filterBar}>
      <TopToolbar>
        <HelpButton />
        <Button
          label="Export"
          disabled={!rows?.length}
          onClick={() => exporter(rows)}
        >
          <ExportIcon />
        </Button>
        <BackButton basePath="/reports" />
      </TopToolbar>

      <Grid container spacing={1}>
        <Grid item xs={12} md={3}>
          <AccountDropDown
            source="accountId"
            label="Account"
            onAccountChange={handleAccountChange}
            validate={required()}
            allowEmpty={true}
            helperText={false}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FilterField
            select
            id="reloadYardCommodityId"
            label="Load Type"
            variant="outlined"
            margin="dense"
            fullWidth
            value={reloadYardCommodityId}
            onChange={(evt) => setReloadYardCommodityId(evt.target.value)}
            helperText={false}
          >
            <MenuItem value="">All</MenuItem>
            {loadTypeOptions.map((lt) => (
              <MenuItem key={lt.id} value={lt.id}>
                {lt.name}
              </MenuItem>
            ))}
          </FilterField>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box mt={-1.5} mr={1}>
            <MonthRangeSlider
              monthRange={monthRange}
              setMonthRange={setMonthRange}
              label="Look-back Months"
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={2} xl={2}>
          <FilterField
            select
            id="chartMetric"
            label="Chart Metric"
            variant="outlined"
            margin="dense"
            fullWidth
            value={chartMetric}
            onChange={(evt) => setChartMetric(evt.target.value)}
            helperText={false}
          >
            <MenuItem value="avgDaysInStorage">Avg Days in Storage</MenuItem>
            <MenuItem value="inventoryTurnover">Inventory Turnover</MenuItem>
            <MenuItem value="throughput">Throughput (per day)</MenuItem>
            <MenuItem value="sellThroughPct">Sell-Through %</MenuItem>
          </FilterField>
        </Grid>

        <Grid item xs={12} md={2} container justifyContent="flex-end">
          <Box mt={1.5}>
            <Button
              label="Run"
              variant="contained"
              color="default"
              size="medium"
              alwaysOn
              onClick={onRun}
              disabled={!accountId || loading}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          {loading && (
            <Typography variant="caption" color="textSecondary">
              {" "}
              Loading…
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const SummaryCards = ({ rows }) => {
  const classes = useStyles();

  const finiteRows = rows.filter((r) => Number.isFinite(r.avgDaysInStorage));
  const avgDays =
    finiteRows.length > 0
      ? finiteRows.reduce((sum, r) => sum + r.avgDaysInStorage, 0) /
        finiteRows.length
      : null;

  const fastest =
    finiteRows.length > 0
      ? [...finiteRows].sort(
          (a, b) => a.avgDaysInStorage - b.avgDaysInStorage,
        )[0]
      : null;

  const slowest =
    finiteRows.length > 0
      ? [...finiteRows].sort(
          (a, b) => b.avgDaysInStorage - a.avgDaysInStorage,
        )[0]
      : null;

  const atRiskCount = rows.filter((r) => r.atRisk).length;

  const CardStat = ({ label, value, subValue }) => (
    <Paper className={classes.kpiCard} variant="outlined">
      <div className={classes.kpiLabel}>{label}</div>
      <div className={classes.kpiValue}>{value}</div>
      {subValue ? (
        <Typography variant="caption" color="textSecondary">
          {subValue}
        </Typography>
      ) : null}
    </Paper>
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={3}>
        <CardStat
          label="Average Days in Storage (Account)"
          value={avgDays == null ? "—" : formatNumber(avgDays, 1)}
          subValue={
            finiteRows.length
              ? `${finiteRows.length} products with completed lots`
              : ""
          }
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardStat
          label="Fastest Product"
          value={fastest ? formatNumber(fastest.avgDaysInStorage, 1) : "—"}
          subValue={fastest ? fastest.name : ""}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardStat
          label="Slowest Product"
          value={slowest ? formatNumber(slowest.avgDaysInStorage, 1) : "—"}
          subValue={slowest ? slowest.name : ""}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardStat
          label="Inventory At Risk"
          value={String(atRiskCount)}
          subValue="Top 25% slowest"
        />
      </Grid>
    </Grid>
  );
};

const TopBottomChart = ({ rows, metric }) => {
  const classes = useStyles();
  const theme = useTheme();

  const metricLabelMap = {
    avgDaysInStorage: "Avg Days in Storage",
    inventoryTurnover: "Inventory Turnover",
    throughput: "Throughput / Day",
    sellThroughPct: "Sell-Through %",
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const val = payload[0]?.value;
    const metricLabel = metricLabelMap[metric] || metric;
    const name = label || payload[0]?.payload?.name || "";

    return (
      <div
        style={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          padding: "8px 10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          color: theme.palette.text.primary,
          maxWidth: 320,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{name}</div>
        <div style={{ color: theme.palette.text.secondary }}>
          {metricLabel}:{" "}
          <span style={{ color: theme.palette.text.primary, fontWeight: 700 }}>
            {formatNumber(val, metric === "sellThroughPct" ? 1 : 1)}
            {metric === "sellThroughPct" ? "%" : ""}
          </span>
        </div>
      </div>
    );
  };

  const withMetric = rows
    .map((r) => {
      const raw = r?.[metric];
      const value = Number.isFinite(raw) ? raw : null;
      return { id: r.id, name: r.name, value };
    })
    .filter((r) => Number.isFinite(r.value));

  const sorted = [...withMetric].sort((a, b) => a.value - b.value);
  const bottom10 = sorted.slice(0, 10);
  const top10 = sorted.slice(Math.max(sorted.length - 10, 0));

  const renderChart = (title, data) => (
    <Paper variant="outlined" className={classes.chartPaper}>
      <Typography variant="subtitle2" gutterBottom align="center">
        {title}
      </Typography>
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 16, right: 16 }}
          >
            <CartesianGrid
              stroke={theme.palette.divider}
              strokeDasharray="3 3"
              opacity={0.55}
            />
            <XAxis
              type="number"
              stroke={theme.palette.divider}
              tick={{ fill: theme.palette.text.primary, fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={200}
              stroke={theme.palette.divider}
              tick={{ fill: theme.palette.text.primary, fontSize: 12 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(255,255,255,0.06)" }}
            />
            <Bar dataKey="value" fill={theme.palette.primary.main} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Typography variant="caption" color="textSecondary">
        {metricLabelMap[metric] || metric}
      </Typography>
    </Paper>
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        {renderChart("Top 10", top10)}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderChart("Bottom 10", bottom10)}
      </Grid>
    </Grid>
  );
};

/**
 * /reports/product-velocity
 *
 * UX principles:
 * - Grid-first: the Datagrid is the primary surface.
 * - Charts are secondary context and NEVER control filtering/sorting.
 * - All calculations happen client-side from ProductStats.stats buckets.
 */
export const ProductVelocityReport = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const history = useHistory();

  // Account context for this report is explicit via AccountDropDown.
  const [accountId, setAccountId] = useState("");
  const reloadYard = window.appUser?.reloadYard;

  const loadTypeOptions = useMemo(() => {
    const items = reloadYard?.activeYardCommodities?.items || [];
    return items.map((itm) => ({
      id: itm.id,
      name: itm?.commodity?.name || itm?.commodityId || itm.id,
    }));
  }, [reloadYard]);

  const [reloadYardCommodityId, setReloadYardCommodityId] = useState("");
  const [monthRange, setMonthRange] = useState(3);
  const [chartMetric, setChartMetric] = useState("avgDaysInStorage");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleAccountChange = (acct) => {
    const selectedAccountId = acct?.id || "";
    setAccountId(selectedAccountId);
    // Clear prior results so users don't confuse old data for a new account.
    setProducts([]);
  };

  const runReport = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const all = [];
      let nextToken = null;
      do {
        const resp = await API.graphql(
          graphqlOperation(productByAccountByActiveByName, {
            accountId,
            activeName: { beginsWith: { active: "true" } },
            limit: 1000,
            nextToken,
          }),
        );
        const page = resp?.data?.productByAccountByActiveByName;
        if (page?.items?.length) all.push(...page.items);
        nextToken = page?.nextToken || null;
        if (all.length > 20000) break; // safety
      } while (nextToken);

      setProducts(all);
    } catch (err) {
      logError("ProductVelocityReport:runReport", err, notify);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const { startMonthKey, endMonthKey } = useMemo(() => {
    // Look-back window ALWAYS ends in the current month.
    const end = startOfMonth(new Date());
    const endKey = monthKeyFromDate(end);
    const start = subMonths(end, Math.max(monthRange - 1, 0));
    const startKey = monthKeyFromDate(start);
    return { startMonthKey: startKey, endMonthKey: endKey };
  }, [monthRange]);

  const rows = useMemo(() => {
    if (!startMonthKey || !endMonthKey) return [];
    const base = buildVelocityRows({
      products,
      reloadYardCommodityId,
      startMonth: startMonthKey,
      endMonth: endMonthKey,
    });
    if (base.length === 0) return [];
    const tiered = applyVelocityTiering(base);
    const ranked = applyDefaultRanking(tiered);

    // Enrich commodity label (no extra joins at render-time).
    const commodityMap = reloadYard?.yardCommodityMap || {};
    return ranked.map((r) => ({
      ...r,
      commodityName:
        commodityMap?.[r.reloadYardCommodityId]?.commodity?.name ||
        commodityMap?.[r.reloadYardCommodityId]?.commodityId ||
        r.reloadYardCommodityId ||
        "",
    }));
  }, [products, reloadYardCommodityId, startMonthKey, endMonthKey, reloadYard]);

  // Heat map ranges (derived from the filtered data set).
  const heatRanges = useMemo(() => {
    const days = rows
      .map((r) => r.avgDaysInStorage)
      .filter((v) => Number.isFinite(v));
    const turns = rows
      .map((r) => r.inventoryTurnover)
      .filter((v) => Number.isFinite(v));
    const minDays = days.length ? Math.min(...days) : 0;
    const maxDays = days.length ? Math.max(...days) : 0;
    const minTurns = turns.length ? Math.min(...turns) : 0;
    const maxTurns = turns.length ? Math.max(...turns) : 0;
    return { minDays, maxDays, minTurns, maxTurns };
  }, [rows]);

  // Inject heat styles into rows (kept as separate fields so Datagrid stays simple).
  const rowsWithHeat = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        _heatDays: Number.isFinite(r.avgDaysInStorage)
          ? heatColor({
              value: r.avgDaysInStorage,
              min: heatRanges.minDays,
              max: heatRanges.maxDays,
              invert: true, // lower days = better (green)
            })
          : "transparent",
        _heatTurns: heatColor({
          value: r.inventoryTurnover,
          min: heatRanges.minTurns,
          max: heatRanges.maxTurns,
          invert: true, // higher turnover = better (green)
        }),
      })),
    [rows, heatRanges],
  );

  // NOTE: Datagrid sorting works entirely client-side on these computed fields.
  const listContext = useList({
    ...props,
    data: rowsWithHeat,
    ids: rowsWithHeat.map(({ id }) => id),
    loading,
    loaded: !loading,
    // Preset behavior: we compute a stable presetRank and sort by it by default.
    // Users can still override by clicking any column header.
    sort: { field: "presetRank", order: "ASC" },
    perPage: 50,
  });

  // Custom render wrappers for heat cells (kept inside the page to avoid global field components).
  const HeatField = ({ label, sortBy, valueKey, heatKey, formatFn }) => (
    <FunctionField
      label={label}
      sortBy={sortBy}
      render={(record) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 6px",
            borderRadius: 6,
            backgroundColor: record?.[heatKey] || "transparent",
          }}
        >
          {formatFn(record?.[valueKey])}
        </span>
      )}
    />
  );

  const handleRowClick = (id, basePath, record) => {
    // Drill-down: go straight to the product edit screen.
    if (!record?.id) return;
    deepRedirect(history, `/products/${record.id}`, "/reports", {});
  };

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Product Velocity" />} />

        <FilterBar
          rows={rows}
          loading={loading}
          accountId={accountId}
          handleAccountChange={handleAccountChange}
          loadTypeOptions={loadTypeOptions}
          reloadYardCommodityId={reloadYardCommodityId}
          setReloadYardCommodityId={setReloadYardCommodityId}
          monthRange={monthRange}
          setMonthRange={setMonthRange}
          chartMetric={chartMetric}
          setChartMetric={setChartMetric}
          onRun={runReport}
        />

        <Box mb={1}>
          <SummaryCards rows={rows} />
        </Box>

        <Box mb={1}>
          <TopBottomChart rows={rows} metric={chartMetric} />
        </Box>

        <Card>
          <CardContent style={{ paddingTop: 8 }}>
            <Typography variant="h6" gutterBottom>
              Product Velocity Grid
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Range: {startMonthKey} → {endMonthKey} ({monthRange} month
              {monthRange === 1 ? "" : "s"})
            </Typography>
          </CardContent>

          {/* Grid-first: primary UX surface */}
          <Datagrid
            {...listContext}
            rowClick={handleRowClick}
            rowStyle={(record) =>
              record?.atRisk ? { borderLeft: "4px solid #d32f2f" } : {}
            }
          >
            <FunctionField
              label="Product Name"
              sortBy="name"
              headerClassName={classes.stickyHeader}
              cellClassName={classes.stickyCol}
              render={(record) => record?.name || ""}
            />
            <TextField source="commodityName" label="Load Type / Commodity" />

            <HeatField
              label="Inventory Turnover"
              sortBy="inventoryTurnover"
              valueKey="inventoryTurnover"
              heatKey="_heatTurns"
              formatFn={(v) => formatNumber(v, 2)}
            />
            <HeatField
              label="Avg Days in Storage"
              sortBy="avgDaysInStorageSort"
              valueKey="avgDaysInStorage"
              heatKey="_heatDays"
              formatFn={(v) => (v == null ? "—" : formatNumber(v, 1))}
            />
            <FunctionField
              label="Throughput (per day)"
              sortBy="throughput"
              render={(record) => formatNumber(record?.throughput, 2)}
            />
            <FunctionField
              label="Sell-Through %"
              sortBy="sellThroughPct"
              render={(record) => `${formatNumber(record?.sellThroughPct, 1)}%`}
            />
            <FunctionField
              label="Avg Inventory On Hand"
              sortBy="avgInventory"
              render={(record) => formatNumber(record?.avgInventory, 1)}
            />
            <FunctionField
              label="Total Shipped (period)"
              sortBy="totalShipped"
              render={(record) => formatNumber(record?.totalShipped, 0)}
            />
            <FunctionField
              label="Total Received (period)"
              sortBy="totalReceived"
              render={(record) => formatNumber(record?.totalReceived, 0)}
            />
            <TextField source="velocityTier" label="Velocity Tier" />
          </Datagrid>

          <Pagination {...listContext} />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};
